import { useState, useEffect } from 'react';
import { useToast } from './use-toast';

declare global {
  interface Window {
    ethereum?: any;
  }
}

export interface Web3State {
  account: string | null;
  chainId: string | null;
  isConnecting: boolean;
  isConnected: boolean;
}

export function useWeb3() {
  const { toast } = useToast();
  const [state, setState] = useState<Web3State>({
    account: null,
    chainId: null,
    isConnecting: false,
    isConnected: false,
  });

  const connect = async () => {
    if (!window.ethereum) {
      toast({
        title: "MetaMask Required",
        description: "Please install MetaMask to connect your wallet.",
        variant: "destructive",
      });
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true }));

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });
      
      const chainId = await window.ethereum.request({
        method: 'eth_chainId',
      });

      setState({
        account: accounts[0],
        chainId,
        isConnecting: false,
        isConnected: true,
      });

      toast({
        title: "Wallet Connected",
        description: `Connected to ${accounts[0].slice(0, 6)}...${accounts[0].slice(-4)}`,
      });
    } catch (error: any) {
      setState(prev => ({ ...prev, isConnecting: false }));
      
      if (error.code === 4001) {
        toast({
          title: "Connection Rejected",
          description: "Please connect your wallet to continue.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Connection Failed",
          description: "Failed to connect to MetaMask. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const disconnect = () => {
    setState({
      account: null,
      chainId: null,
      isConnecting: false,
      isConnected: false,
    });
    
    toast({
      title: "Wallet Disconnected",
      description: "Your wallet has been disconnected.",
    });
  };

  const switchToMainnet = async () => {
    if (!window.ethereum) return;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x1' }], // Ethereum mainnet
      });
    } catch (error: any) {
      if (error.code === 4902) {
        toast({
          title: "Network Switch Required",
          description: "Please switch to Ethereum mainnet in MetaMask.",
          variant: "destructive",
        });
      }
    }
  };

  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        disconnect();
      } else {
        setState(prev => ({ ...prev, account: accounts[0] }));
      }
    };

    const handleChainChanged = (chainId: string) => {
      setState(prev => ({ ...prev, chainId }));
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    // Check if already connected
    window.ethereum
      .request({ method: 'eth_accounts' })
      .then((accounts: string[]) => {
        if (accounts.length > 0) {
          window.ethereum
            .request({ method: 'eth_chainId' })
            .then((chainId: string) => {
              setState({
                account: accounts[0],
                chainId,
                isConnecting: false,
                isConnected: true,
              });
            });
        }
      });

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    switchToMainnet,
    isMainnet: state.chainId === '0x1',
  };
}